import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { rm } from 'utils';
import { useToggle } from 'hooks';
import { IRelatedProduct } from 'models/BasicProduct';
import { Button, LoaderButton } from 'components/Button';
import Brand from 'components/Shared/Brand';
import { Title } from 'components/Shared/Product';
import Image from 'components/Shared/Image';
import Markdown from 'components/Shared/Markdown';
import { notifyAddToCart, NotifyLink } from 'components/Shared/NotifyLink';
import { isMobile } from 'components/Shared/responsive';
import { DrawerProduct } from 'views/ProductPage/drawerProduct';
import { addToCart, useECPAddToCart } from 'components/Cart/actions';
import styles from './style.scss';
import { forwardRef, useState } from 'react';
import { IconCheckmark } from 'components/Shared/Icon';
import { CardPrice } from 'components/Shared/Price/themes/BGH/cardPrice';
import { selectShouldUseGecpHeader } from 'store/appShell/selector';

const IMAGE_SIZE_MOBILE = { width: 100, height: 100 };
const IMAGE_SIZE_DESKTOP = { width: 170, height: 170 };

interface OwnProps extends IRelatedProduct {
  className?: string;
  showQuantity?: boolean;
  classNames?: {
    imageWrapper?: string;
    title?: string;
  };
  onDrawerClick?: (open: boolean) => void;
  gtmListName?: string;
  gtmOnItemClick: (product: IRelatedProduct) => void;
}

const useAddToCart = ({
  ticket,
  quantity,
  gtmListName
}: Pick<OwnProps, 'ticket' | 'quantity' | 'gtmListName'>) => {
  const dispatch = useDispatch();
  const { addToCart: addToCartAsync } = useECPAddToCart();
  const shouldUseGecpHeader = useSelector(selectShouldUseGecpHeader);

  return async (productId: string) => {
    const lowestQuantity = quantity?.lowest ? quantity.lowest : 1;
    notifyAddToCart(ticket);

    if (shouldUseGecpHeader) {
      return addToCartAsync({
        productId: productId,
        quantity: lowestQuantity,
        gtmItemList: gtmListName,
        includeAddons: false
      });
    }

    return dispatch(
      addToCart({
        input: { id: productId, quantity: lowestQuantity },
        gtmListName: gtmListName
      })
    );
  };
};

export const AccessoryItem = forwardRef<HTMLDivElement, OwnProps>(
  ({ className, classNames = {}, onDrawerClick, gtmListName, gtmOnItemClick, ...product }, ref) => {
    const {
      title,
      titleSplitPos,
      productImage,
      brandLogo,
      shortDescription,
      variantAttributes,
      price,
      isFamily,
      url,
      productId,
      ticket,
      quantity
    } = product;

    const isPhone = useSelector(isMobile);
    const add = useAddToCart({ ticket, quantity, gtmListName });

    const [drawerOpen, _toggleDrawer] = useToggle(false);
    const toggleDrawer = (value: boolean) => () => {
      onDrawerClick && onDrawerClick(value);
      _toggleDrawer(value);
    };
    const [hasBought, setHasBought] = useState(false);

    const handleClick = async () => {
      add(productId).then(() => {
        if (!hasBought) {
          setHasBought(true);
        }
      });
    };

    const buttonText =
      variantAttributes?.length === 1
        ? rm('accessory_choose_attribute', variantAttributes[0].name)
        : rm('accessory_choose_variant');

    const size = isPhone ? IMAGE_SIZE_MOBILE : IMAGE_SIZE_DESKTOP;

    const handleItemClick = () => {
      if (gtmOnItemClick) {
        gtmOnItemClick(product);
      }
    };

    return (
      <>
        <div ref={ref} className={cn(className, styles.accessoryItem)}>
          <div className={cn(styles.imageWrapper, classNames.imageWrapper)}>
            <NotifyLink className={styles.link} url={url} ticket={ticket} onClick={handleItemClick}>
              <Brand className={styles.brandImage} {...brandLogo} appearance="small" />
              <Image className={styles.productImage} paddingBottom {...size} {...productImage} />
            </NotifyLink>
          </div>

          <Title
            classNames={{
              outerWrapper: cn(styles.title, classNames.title),
              innerWrapper: styles.inner
            }}
            isCategoryBold
            title={title}
            titleSplitPos={titleSplitPos}
            url={url}
            ticket={ticket}
            onClick={handleItemClick}
          />

          <div className={styles.priceContainer}>
            <CardPrice price={price} page="accessories" quantityUnit={quantity?.unit} />
            {isFamily ? (
              <Button
                onClick={toggleDrawer(true)}
                className={styles.button}
                size="medium"
                appearance="quaternary"
                textTransform="uppercase"
              >
                {buttonText}
              </Button>
            ) : (
              <LoaderButton
                className={styles.button}
                size="medium"
                appearance="quaternary"
                textTransform="uppercase"
                onClick={handleClick}
              >
                {hasBought ? <IconCheckmark /> : rm('product_accessories_buy')}
              </LoaderButton>
            )}
          </div>
          <Markdown className={styles.description} {...shortDescription} />
        </div>

        {isFamily && (
          <DrawerProduct
            product={product}
            title={buttonText}
            isOpen={drawerOpen}
            onClose={toggleDrawer(false)}
          />
        )}
      </>
    );
  }
);
